import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { PatientSectionListService } from '@modules/dashboard/patients/patients-section-list.service';
import { ArchivedBadgeComponent } from '@modules/shared/archived-badge/archived-badge.component';
import { ShowIfClientIsGamEnabledDirective } from '@modules/shared/directives/show-if-client-is-gam-enabled.directive';
import { LottieLabelComponent } from '@modules/shared/lottie-label/lottie-label.component';
import { PAGINATED_LIST_SOURCE, PaginatedListComponent } from '@modules/shared/paginated-list/paginated-list.component';
import { Store } from '@ngrx/store';
import { Patient } from '@pixacare/domain';
import { SectionListData } from '@pixacare/shared';
import { BasePagination } from '@shared/base-pagination';
import { SortOrderStrategy } from '@shared/models/enums/sort-order-strategy.enum';
import { selectClientCode } from '@shared/store/router/router.selectors';
import { map, Observable, of, switchMap } from 'rxjs';
import { MenuAction } from 'src/app/shared/models/menu-actions/menu-action';
import { PatientCardComponent } from '../patient-card/patient-card.component';
import { PatientLinkQuickActionComponent } from '../patient-link-quick-action/patient-link-quick-action.component';
import { PatientLinkDirective } from '../patient-link.directive';

@Component({
  selector: 'pxc-patient-list',
  templateUrl: './patient-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PatientLinkDirective,
    PatientCardComponent,
    ArchivedBadgeComponent,
    PaginatedListComponent,
    TranslocoDirective,
    LottieLabelComponent,
    PatientLinkQuickActionComponent,
    ShowIfClientIsGamEnabledDirective,
  ],
  providers: [PatientSectionListService],
  host: {
    class: 'block h-full',
  },
})
export class PatientListComponent {
  @Input() patients: Patient[] = [];
  @Input() actions: MenuAction<Patient>[] = null;
  @Input() order: SortOrderStrategy = SortOrderStrategy.LAST_ACTIVITY;

  private readonly clientCode$ = inject(Store).select(selectClientCode);
  private readonly pagination = inject<BasePagination<Patient>>(PAGINATED_LIST_SOURCE, { optional: true });
  private readonly patientSectionListService = inject(PatientSectionListService);

  get collection$(): Observable<SectionListData<Patient>> {
    return this.getPatientsCollection().pipe(
      map((patients) => this.patientSectionListService.getPatientSectionList(patients, this.order)),
    );
  }

  private getPatientsCollection(): Observable<Patient[]> {
    if (this.pagination) {
      return this.clientCode$.pipe(
        switchMap((clientCode) => this.pagination.select({ clientCode }).pipe(map((collection) => collection.data))),
      );
    }
    return of(this.patients);
  }
}
